import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ShipService } from 'src/app/services/ship/ship.service';
import { ShipModalService } from 'src/app/services/ship-modal/ship-modal.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { PortService } from 'src/app/services/port/port.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-ship-add-edit',
  templateUrl: './ship-add-edit.component.html',
  styleUrls: ['./ship-add-edit.component.scss']
})
export class ShipAddEditComponent extends DestructorComponent implements OnInit {

  @Output() shipAdded = new EventEmitter<void>();
  @Output() shipUpdated = new EventEmitter<void>();

  shipForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedShip: any = null;
  countryList: any[] = [];
  filteredCountryList: any[] = [];
  countrySearchTerm: string = '';
  showCountryDropdown: boolean = false;

  // Sailing Port properties
  sailingPortList: any[] = [];
  filteredSailingPortList: any[] = [];
  isLoadingSailingPorts: boolean = false;
  sailingPortSearchTerm: string = '';
  showSailingPortDropdown: boolean = false;
  selectedSailingPort: any = null;

  // Destination Port properties
  destinationPortList: any[] = [];
  filteredDestinationPortList: any[] = [];
  isLoadingDestinationPorts: boolean = false;
  destinationPortSearchTerm: string = '';
  showDestinationPortDropdown: boolean = false;
  selectedDestinationPort: any = null;

  // Selected destination country
  selectedDestinationCountry: any = null;

  // Company search properties
  companySearchTerm: string = '';
  companySearchResults: any[] = [];
  selectedCompany: any = null;
  showCompanyDropdown: boolean = false;
  isCompanySearching: boolean = false;
  private companySearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _shipService: ShipService,
    private _shipModalService: ShipModalService,
    private _globalStateService: GlobalStateService,
    private _portService: PortService,
    private _vendorService: VendorService
  ) {
    super();
    this.initializeForm();
    this.setupCompanySearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCountries();
    this.loadSailingPorts();
  }

  private loadCountries(): void {
    // Subscribe to countries from GlobalStateService
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countryList = countries || [];
        this.filteredCountryList = [...this.countryList];
      })
    );
  }

  private initializeForm(): void {
    this.shipForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      ship_id: [{value: '', disabled: true}],
      company: ['', [Validators.required]],
      shipping_company_id: [''],
      voyage_no: ['', [Validators.required]],
      sailing_date: ['', [Validators.required]],
      sailing_port: ['', [Validators.required]],
      sailing_port_id: ['', [Validators.required]],
      destination_port: ['', [Validators.required]],
      destination_port_id: ['', [Validators.required]],
      destination_country: ['', [Validators.required]],
      destination_country_id: ['', [Validators.required]],
      arrival_date: ['', [Validators.required]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_ship_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceShip = this._shipModalService.getSelectedShip();
        if (serviceShip) {
          this.isEditMode = true;
          this.selectedShip = serviceShip;
          this.populateForm(this.selectedShip);
        } else {
          this.isEditMode = false;
          this.selectedShip = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._shipModalService.clearSelectedShip();
        this.resetForm();
      });
    }
  }

  private populateForm(ship: any): void {
    if (ship) {
      this.shipForm.patchValue({
        name: ship.name || '',
        ship_id: ship.ship_id || '',
        company: ship.company || '',
        shipping_company_id: ship.shipping_company_id || '',
        voyage_no: ship.voyage_no || '',
        sailing_date: ship.sailing_date ? this.formatDateForInput(ship.sailing_date) : '',
        sailing_port: ship.sailing_port || '',
        sailing_port_id: ship.sailing_port_id || '',
        destination_port: ship.destination_port || '',
        destination_port_id: ship.destination_port_id || '',
        destination_country: ship.destination_country || '',
        destination_country_id: ship.destination_country_id || '',
        arrival_date: ship.arrival_date ? this.formatDateForInput(ship.arrival_date) : ''
      });

      // Set company search term
      if (ship.company) {
        this.companySearchTerm = ship.company;
        this.selectedCompany = { company_name: ship.company, _id: ship.shipping_company_id };
      }

      // Set sailing port search term and selected port
      if (ship.sailing_port) {
        this.sailingPortSearchTerm = ship.sailing_port;
        this.selectedSailingPort = { name: ship.sailing_port, _id: ship.sailing_port_id };
      }

      // Set selected destination country and load ports
      if (ship.destination_country) {
        this.selectedDestinationCountry = this.countryList.find(c => c.name === ship.destination_country);
        if (this.selectedDestinationCountry) {
          this.countrySearchTerm = this.selectedDestinationCountry.name;
          this.loadDestinationPorts(this.selectedDestinationCountry.code);
        }
      }

      // Set destination port search term and selected port
      if (ship.destination_port) {
        this.destinationPortSearchTerm = ship.destination_port;
        this.selectedDestinationPort = { name: ship.destination_port, _id: ship.destination_port_id };
      }
    }
  }

  private formatDateForInput(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  onSubmit(): void {
    if (this.shipForm.valid) {
      this.isLoading = true;
      const formData = this.shipForm.value;

      if (this.isEditMode && this.selectedShip) {
        this.updateShip(formData);
      } else {
        this.addShip(formData);
      }
    } else {
      this.markFormGroupTouched(this.shipForm);
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }

  private addShip(formData: any): void {
    this.subscriptions.push(
      this._shipService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Ship has been added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.shipAdded.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add ship.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error adding ship:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to add ship. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateShip(formData: any): void {
    const shipId = this.selectedShip._id || this.selectedShip.id;
    this.subscriptions.push(
      this._shipService.UpdateData(shipId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Ship has been updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.shipUpdated.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update ship.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error updating ship:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to update ship. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_ship_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private resetForm(): void {
    this.shipForm.reset({
      name: '',
      ship_name: '',
      imo_number: '',
      flag: '',
      departure_port: '',
      arrival_port: '',
      departure_date: '',
      arrival_date: '',
      status: 'active',
      description: '',
      capacity: '',
      vessel_type: '',
      year_built: '',
      length: '',
      width: '',
      gross_tonnage: '',
      net_tonnage: ''
    });
    this.isEditMode = false;
    this.selectedShip = null;
    this.isLoading = false;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Load all sailing ports
  private loadSailingPorts(): void {
    this.isLoadingSailingPorts = true;
    this.subscriptions.push(
      this._portService.GetData('1', '', { from_port: true, paginate: false }).subscribe({
        next: (response: any) => {
          this.isLoadingSailingPorts = false;
          if (response.status && response.data) {
            this.sailingPortList = response.data.data || response.data || [];
            this.filteredSailingPortList = [...this.sailingPortList];
          } else {
            this.sailingPortList = [];
            this.filteredSailingPortList = [];
          }
        },
        error: () => {
          this.isLoadingSailingPorts = false;
          this.sailingPortList = [];
          this.filteredSailingPortList = [];
        }
      })
    );
  }

  // Filter sailing ports based on search term
  onSailingPortSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.sailingPortSearchTerm = target.value;

    if (this.sailingPortSearchTerm.trim() === '') {
      this.filteredSailingPortList = [...this.sailingPortList];
    } else {
      const searchLower = this.sailingPortSearchTerm.toLowerCase();
      this.filteredSailingPortList = this.sailingPortList.filter(port =>
        port.name.toLowerCase().includes(searchLower)
      );
    }
  }

  // Handle sailing port selection
  selectSailingPort(port: any): void {
    this.selectedSailingPort = port;
    this.sailingPortSearchTerm = port.name;
    this.shipForm.patchValue({
      sailing_port: port.name,
      sailing_port_id: port._id || port.id
    });
    this.showSailingPortDropdown = false;
  }

  // Show sailing port dropdown
  onSailingPortFocus(): void {
    this.showSailingPortDropdown = true;
    if (this.sailingPortSearchTerm.trim() === '') {
      this.filteredSailingPortList = [...this.sailingPortList];
    }
  }

  // Hide sailing port dropdown
  onSailingPortBlur(): void {
    setTimeout(() => {
      this.showSailingPortDropdown = false;
    }, 200);
  }

  // Clear sailing port selection
  clearSailingPort(): void {
    this.selectedSailingPort = null;
    this.sailingPortSearchTerm = '';
    this.filteredSailingPortList = [...this.sailingPortList];
    this.shipForm.patchValue({
      sailing_port: '',
      sailing_port_id: ''
    });
  }

  // Load destination ports based on country
  private loadDestinationPorts(countryCode: string): void {
    if (!countryCode) {
      this.destinationPortList = [];
      this.filteredDestinationPortList = [];
      return;
    }

    this.isLoadingDestinationPorts = true;
    this.subscriptions.push(
      this._portService.GetData('1', '', {
        country_code: countryCode,
        paginate: false
      }).subscribe({
        next: (response: any) => {
          this.isLoadingDestinationPorts = false;
          if (response.status && response.data) {
            this.destinationPortList = response.data.data || response.data || [];
            this.filteredDestinationPortList = [...this.destinationPortList];
          } else {
            this.destinationPortList = [];
            this.filteredDestinationPortList = [];
          }
        },
        error: () => {
          this.isLoadingDestinationPorts = false;
          this.destinationPortList = [];
          this.filteredDestinationPortList = [];
        }
      })
    );
  }

  // Filter destination ports based on search term
  onDestinationPortSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.destinationPortSearchTerm = target.value;

    if (this.destinationPortSearchTerm.trim() === '') {
      this.filteredDestinationPortList = [...this.destinationPortList];
    } else {
      const searchLower = this.destinationPortSearchTerm.toLowerCase();
      this.filteredDestinationPortList = this.destinationPortList.filter(port =>
        port.name.toLowerCase().includes(searchLower)
      );
    }
  }

  // Handle destination port selection
  selectDestinationPort(port: any): void {
    this.selectedDestinationPort = port;
    this.destinationPortSearchTerm = port.name;
    this.shipForm.patchValue({
      destination_port: port.name,
      destination_port_id: port._id || port.id
    });
    this.showDestinationPortDropdown = false;
  }

  // Show destination port dropdown
  onDestinationPortFocus(): void {
    if (!this.selectedDestinationCountry) {
      return;
    }
    this.showDestinationPortDropdown = true;
    if (this.destinationPortSearchTerm.trim() === '') {
      this.filteredDestinationPortList = [...this.destinationPortList];
    }
  }

  // Hide destination port dropdown
  onDestinationPortBlur(): void {
    setTimeout(() => {
      this.showDestinationPortDropdown = false;
    }, 200);
  }

  // Clear destination port selection
  clearDestinationPort(): void {
    this.selectedDestinationPort = null;
    this.destinationPortSearchTerm = '';
    this.filteredDestinationPortList = [...this.destinationPortList];
    this.shipForm.patchValue({
      destination_port: '',
      destination_port_id: ''
    });
  }

  // Filter countries based on search term
  onCountrySearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.countrySearchTerm = target.value;

    if (this.countrySearchTerm.trim() === '') {
      this.filteredCountryList = [...this.countryList];
    } else {
      const searchLower = this.countrySearchTerm.toLowerCase();
      this.filteredCountryList = this.countryList.filter(country =>
        country.name.toLowerCase().includes(searchLower) ||
        (country.code && country.code.toLowerCase().includes(searchLower))
      );
    }
  }

  // Handle country selection
  selectCountry(country: any): void {
    this.selectedDestinationCountry = country;
    this.countrySearchTerm = country.name;
    this.shipForm.patchValue({
      destination_country: country.name,
      destination_country_id: country._id || country.id
    });
    this.showCountryDropdown = false;

    // Load ports for the selected country
    this.loadDestinationPorts(country.code);

    // Clear destination port when country changes
    this.clearDestinationPort();
  }

  // Show country dropdown
  onCountryFocus(): void {
    this.showCountryDropdown = true;
    if (this.countrySearchTerm.trim() === '') {
      this.filteredCountryList = [...this.countryList];
    }
  }

  // Hide country dropdown
  onCountryBlur(): void {
    setTimeout(() => {
      this.showCountryDropdown = false;
    }, 200);
  }

  // Clear country selection
  clearCountry(): void {
    this.selectedDestinationCountry = null;
    this.countrySearchTerm = '';
    this.filteredCountryList = [...this.countryList];
    this.shipForm.patchValue({
      destination_country: '',
      destination_country_id: ''
    });

    // Clear destination port as well
    this.clearDestinationPort();
    this.destinationPortList = [];
    this.filteredDestinationPortList = [];
  }

  // Company search methods
  private setupCompanySearchDebounce(): void {
    this.companySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchCompanies(searchTerm);
    });
  }

  onCompanySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.companySearchTerm = target.value;
    this.selectedCompany = null;

    // Update the form control with the typed value
    this.shipForm.patchValue({
      company: this.companySearchTerm,
      shipping_company_id: ''
    });

    if (this.companySearchTerm.length >= 2) {
      this.companySearchSubject.next(this.companySearchTerm);
      this.showCompanyDropdown = true;
    } else {
      this.showCompanyDropdown = false;
      this.companySearchResults = [];
    }
  }

  onCompanyInputFocus(): void {
    if (this.companySearchResults.length > 0) {
      this.showCompanyDropdown = true;
    }
  }

  onCompanyInputBlur(): void {
    setTimeout(() => {
      this.showCompanyDropdown = false;
    }, 200);
  }

  private searchCompanies(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.companySearchResults = [];
      this.showCompanyDropdown = false;
      return;
    }

    this.isCompanySearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'shipping' }).subscribe({
        next: (response: any) => {
          this.isCompanySearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.companySearchResults = response.data.data || response.data || [];
            this.showCompanyDropdown = this.companySearchResults.length > 0;
          } else {
            this.companySearchResults = [];
            this.showCompanyDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCompanySearching = false;
          console.error('Error searching companies:', error);
          this.companySearchResults = [];
          this.showCompanyDropdown = false;
        }
      })
    );
  }

  selectCompany(company: any): void {
    this.selectedCompany = company;
    this.companySearchTerm = this.getCompanyDisplayName(company);

    this.shipForm.patchValue({
      company: this.companySearchTerm,
      shipping_company_id: company._id || company.id
    });

    this.showCompanyDropdown = false;
  }

  private getCompanyDisplayName(company: any): string {
    return company.company_name || 'Unknown Company';
  }

  // Getter methods for form validation
  get name() { return this.shipForm.get('name'); }
  get ship_id() { return this.shipForm.get('ship_id'); }
  get company() { return this.shipForm.get('company'); }
  get voyage_no() { return this.shipForm.get('voyage_no'); }
  get sailing_date() { return this.shipForm.get('sailing_date'); }
  get sailing_port() { return this.shipForm.get('sailing_port'); }
  get destination_port() { return this.shipForm.get('destination_port'); }
  get destination_country() { return this.shipForm.get('destination_country'); }
  get arrival_date() { return this.shipForm.get('arrival_date'); }
}
